import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ROUTES } from '@/config/routes';
import { PasswordResetService, PasswordResetServiceError } from '@/services/passwordResetService';

interface FormErrors {
  email?: string;
  general?: string;
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Helper functions for translations
  const getForgotPassword = (key: string) => t(`auth.forgotPassword.${key}`);
  const getForgotPasswordForm = (key: string) => t(`auth.forgotPassword.form.${key}`);
  const getForgotPasswordSuccess = (key: string) => t(`auth.forgotPassword.success.${key}`);
  const getForgotPasswordErrors = (key: string) => t(`auth.forgotPassword.errors.${key}`);

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
      return getForgotPasswordErrors('emailRequired');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return getForgotPasswordErrors('invalidEmail');
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({});

    // Validate email
    const emailError = validateEmail(email);
    if (emailError) {
      setFormErrors({ email: emailError });
      return;
    }

    setIsLoading(true);

    try {
      await PasswordResetService.requestPasswordReset(email);
      setIsSuccess(true);
    } catch (error) {
      if (error instanceof PasswordResetServiceError) {
        if (error.status === 429) {
          setFormErrors({ general: getForgotPasswordErrors('tooManyRequests') });
        } else {
          setFormErrors({ general: getForgotPasswordErrors('sendFailed') });
        }
      } else {
        setFormErrors({ general: getForgotPasswordErrors('sendFailed') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    setFormErrors({});

    try {
      await PasswordResetService.requestPasswordReset(email);
    } catch (error) {
      if (error instanceof PasswordResetServiceError) {
        if (error.status === 429) {
          setFormErrors({ general: getForgotPasswordErrors('tooManyRequests') });
        } else {
          setFormErrors({ general: getForgotPasswordErrors('sendFailed') });
        }
      } else {
        setFormErrors({ general: getForgotPasswordErrors('sendFailed') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-4 sm:px-6 lg:px-8">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-blue-600/20 backdrop-blur-3xl" />
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

        <motion.div
          className="relative w-full max-w-md space-y-8 rounded-2xl border border-slate-700/50 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-xl"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Success Icon */}
          <motion.div
            className="flex justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex size-16 items-center justify-center rounded-full bg-green-500/20">
              <CheckCircle className="size-8 text-green-400" />
            </div>
          </motion.div>

          {/* Success Content */}
          <motion.div
            className="space-y-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h1 className="text-2xl font-bold text-white">{getForgotPasswordSuccess('title')}</h1>
            <p className="text-slate-300">{getForgotPasswordSuccess('subtitle')}</p>
            <div className="rounded-lg border border-slate-600 bg-slate-700/50 p-4 text-sm text-slate-300">
              <Mail className="mr-2 inline size-4" />
              {getForgotPasswordSuccess('instructions')}
            </div>
          </motion.div>

          {/* Error Display */}
          {formErrors.general && (
            <motion.div
              className="flex items-center rounded-lg border border-red-500/50 bg-red-500/20 p-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              aria-live="polite"
            >
              <AlertCircle className="mr-3 size-5 shrink-0 text-red-400" />
              <span className="text-sm text-red-300">{formErrors.general}</span>
            </motion.div>
          )}

          {/* Actions */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="text-center text-sm text-slate-400">
              {getForgotPasswordSuccess('noEmail')}{' '}
              <button
                onClick={handleResend}
                disabled={isLoading}
                className="rounded-md px-1 text-primary-400 transition-colors hover:text-primary-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getForgotPasswordSuccess('resendLink')}
              </button>
            </div>

            <Link
              to={ROUTES.AUTH.LOGIN}
              className="block w-full rounded-lg bg-primary-600 py-3 text-center font-semibold text-white shadow-lg transition-all duration-200 hover:bg-primary-700 hover:shadow-xl focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getForgotPasswordSuccess('backToLogin')}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-4 sm:px-6 lg:px-8">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-blue-600/20 backdrop-blur-3xl" />
      <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

      <motion.div
        className="relative w-full max-w-md space-y-8 rounded-2xl border border-slate-700/50 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-xl"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <motion.div
          className="space-y-2 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1 className="text-3xl font-bold text-white">{getForgotPassword('title')}</h1>
          <p className="text-slate-300">{getForgotPassword('subtitle')}</p>
        </motion.div>

        {/* Error Display */}
        {formErrors.general && (
          <motion.div
            className="flex items-center rounded-lg border border-red-500/50 bg-red-500/20 p-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="mr-3 size-5 shrink-0 text-red-400" />
            <span className="text-sm text-red-300">{formErrors.general}</span>
          </motion.div>
        )}

        {/* Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          noValidate
        >
          {/* Email Field */}
          <div className="relative">
            <div className="relative">
              <Input
                type="email"
                id="email"
                name="email"
                value={email}
                placeholder=" "
                required
                onChange={e => setEmail(e.target.value)}
                className={`peer w-full rounded-lg border bg-slate-800/50 px-4 py-3 text-white backdrop-blur-sm transition-colors placeholder:text-transparent focus:outline-none ${
                  formErrors.email
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-slate-600 focus:border-primary-500'
                }`}
                aria-describedby={formErrors.email ? 'email-error' : undefined}
              />
              <label
                htmlFor="email"
                className="text-shadow-sm pointer-events-none absolute left-4 top-3 z-10 origin-left text-slate-400 transition-all duration-200 ease-out [text-shadow:_0_1px_3px_rgb(15_23_42_/_0.8)] peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-8 peer-focus:scale-75 peer-focus:text-primary-400 peer-[:not(:placeholder-shown)]:-translate-y-8 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:text-slate-300"
              >
                {getForgotPasswordForm('emailLabel')}
              </label>
            </div>
            {formErrors.email && (
              <p id="email-error" className="mt-2 flex items-center text-sm text-red-400">
                <AlertCircle className="mr-1 size-4" />
                {formErrors.email}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-primary-700 hover:to-primary-600 hover:shadow-xl focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="mr-2 size-5 animate-spin rounded-full border-b-2 border-white"></div>
                {getForgotPasswordForm('submittingButton')}
              </div>
            ) : (
              getForgotPasswordForm('submitButton')
            )}
          </Button>
        </motion.form>

        {/* Back to Login */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Link
            to={ROUTES.AUTH.LOGIN}
            className="inline-flex items-center rounded-md px-1 text-sm text-primary-400 transition-colors hover:text-primary-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
          >
            <ArrowLeft className="mr-1 size-4" />
            {getForgotPasswordForm('backToLogin')}
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
}
